import { Button } from "@/src/components/atom/Button";
import Checkbox from "@/src/components/atom/Checkbox";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import Typo from "@/src/components/atom/Typo";
import CallOut from "@/src/components/molecule/CallOut";
import Flex from "@/src/components/molecule/Flex";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import useAlert from "@/src/hooks/useAlert";
import { authApi } from "@/src/store/apis/auth";
import { isUndefined } from "@/src/utils/is";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CreditSelectTabState } from "../..";
import { useTabContext } from "../../context/creditContext";
import { useSearchParams } from "react-router-dom";
import colorSet, { ColorType } from "@/src/styles/color";
import { FontSizeType } from "@/src/styles/typography";
import {
  formatCreditCardExpiryDate,
  formatCreditCardMasking,
  formatNumberWithDecimal,
} from "@/src/utils/format";
import Badge from "@/src/components/atom/Badge";
import NumberStepper, {
  NumberStepperRef,
} from "@/src/components/molecule/NumberStepper";
import { useCreateCreditMutation } from "@/src/store/apis/credit";
import { usePurchaseRefundQuery } from "../PurchaseRefund";
import { useGetPaymentMethodsQuery } from "@/src/store/apis/subscription";

// 가격 계산 로직 수정
export const CREDIT_UNIT_PRICE = 27.27; // 크레딧 단가
export const VAT_RATE = 0.1; // 부가세율 10%
interface BuyCreditDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onTabChange: (tab: CreditSelectTabState) => void;
}

function BuyCreditDialog({
  open,
  onOpenChange,
  onTabChange,
}: BuyCreditDialogProps) {
  const { t } = useTranslation();
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const numberStepperRef = useRef<NumberStepperRef>(null);

  const tabContext = useTabContext(CreditSelectTabState.PURCHASE_REFUND);
  const { pagination, onPagination, onSortChange, onOrderChange } = tabContext;

  const [isConfirm, setIsConfirm] = useState(false);
  const [creditAmount, setCreditAmount] = useState(1);

  const selectTab = searchParams.get("selectTab");

  const noticeList = [
    t("common:buyCreditNotice1"),
    t("common:buyCreditNotice2"),
    t("common:buyCreditNotice3"),
    t("common:buyCreditNotice4"),
  ];

  // API
  const [createCredit, { isLoading }] = useCreateCreditMutation();
  const { isCorporateManager, businessNumber } =
    authApi.endpoints.getSession.useQueryState(undefined, {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isCorporateManager: isStable
            ? currentData.row.exporterUserType === "CORPORATE_MANAGER"
            : false,
          businessNumber: isStable
            ? currentData.row.exporter.businessNumber
            : "",
        };
      },
    });
  const { data: paymentMethodsData } = useGetPaymentMethodsQuery({
    page: 1,
    pageSize: 2,
  });
  
  const payments = paymentMethodsData?.rows || [];

  const representativePayment = payments?.find(
    (payment) => payment.isRepresentative
  );

  const { fetchPurchaseHistory } = usePurchaseRefundQuery();

  // 크레딧 수량에 따른 할인율 계산 함수
  const getDiscountRate = (amount: number) => {
    if (amount >= 30 && amount <= 50) return 30;
    if (amount >= 20 && amount <= 29) return 20;
    if (amount >= 10 && amount <= 19) return 10;
    return 0; // 1-9개: 0% 할인
  };

  const basePrice = CREDIT_UNIT_PRICE * creditAmount; // 기본 가격
  const discountRate = getDiscountRate(creditAmount); // 할인율
  const discountAmount = basePrice * (discountRate / 100); // 할인 금액
  const priceAfterDiscount = basePrice - discountAmount; // 할인 적용 후 가격
  const vatAmount = priceAfterDiscount * VAT_RATE; // 할인 적용 후 가격에 대한 부가세
  const totalAmount = priceAfterDiscount + vatAmount; // 총 결제 금액

  const priceData = [
    {
      label: t("common:creditPrice"),
      labelTypoType: "b7m",
      labelColorType: "gray6",
      value: basePrice,
      valueTypoType: "b7r",
    },
    {
      label: t("common:vat"),
      labelTypoType: "b9r",
      labelColorType: "gray6",
      value: vatAmount,
      valueTypoType: "b9r",
    },
    {
      label: `${t("common:discount")} (${discountRate}%)`,
      labelTypoType: "b9r",
      labelColorType: "gray6",
      value: discountAmount,
      valueTypoType: "b9r",
      isDiscount: true,
      isVisible: creditAmount >= 10,
    },
    {
      label: t("common:totalPaymentAmount"),
      labelTypoType: "b7m",
      labelColorType: "gray6",
      value: totalAmount,
      valueTypoType: "h7",
      isDividerBefore: true,
    },
  ] as {
    label: string;
    labelTypoType: FontSizeType;
    labelColorType: ColorType;
    value: number;
    valueTypoType: FontSizeType;
    isDividerBefore?: boolean;
    isDiscount?: boolean;
    isVisible?: boolean;
  }[];

  const creditCardData = [
    {
      label: t("common:cardInformation"),
      labelTypoType: "b7m",
      labelColorType: "gray6",
      value: formatCreditCardMasking(representativePayment?.cardNumber || ""),
      valueTypoType: "b7r",
      isBusiness: businessNumber === representativePayment?.userInfo,
      isBusinessBadge: true,
    },
    {
      label: t("common:cardExpirationDate"),
      labelTypoType: "b7m",
      labelColorType: "gray6",
      value: formatCreditCardExpiryDate(
        representativePayment?.expiryDate || ""
      ),

      valueTypoType: "b7r",
    },
  ] as {
    label: string;
    labelTypoType: FontSizeType;
    labelColorType: ColorType;
    value: string;
    valueTypoType: FontSizeType;
    isBusiness?: boolean;
    isBusinessBadge?: boolean;
  }[];

  const handleConfirmClick = async () => {
    if (!representativePayment?.id) {
      return;
    }

    try {
      await createCredit({
        totalCredit: creditAmount,
        availableCredit: creditAmount,
        creditType: "ONE_TIME_PURCHASE",
        paymentMethodId: representativePayment.id,
        price: Number(formatNumberWithDecimal(totalAmount).replace(/,/g, "")),
      }).unwrap();

      alert.showAlert({
        type: "success",
        message: t("alert:buyCreditSuccess"),
      });

      onTabChange(CreditSelectTabState.PURCHASE_REFUND);
      const newParams = new URLSearchParams(
        CreditSelectTabState.PURCHASE_REFUND
      );
      newParams.set("selectTab", CreditSelectTabState.PURCHASE_REFUND);
      setSearchParams(newParams, { replace: true });

      if (selectTab === CreditSelectTabState.PURCHASE_REFUND) {
        await fetchPurchaseHistory({
          page: 1,
          pageSize: pagination.pageSize,
        });
      }

      onPagination({ page: 1, pageSize: pagination.pageSize });
      onSortChange(undefined);
      onOrderChange(undefined);
      onOpenChange(false);
    } catch (e: any) {
      const message = e.data?.message || e.message || "";
      
      // Check if it's a card PIN error
      if (message && (message.includes("PIN") || message.includes("password") || message.includes("비밀번호"))) {
        alert.showAlert({
          type: "error",
          message: t("alert:cardPinError"),
        });
      } 
      // Check if it's a card expiry error
      else if (message && (message.includes("expir") || message.includes("유효기간") || message.includes("期限") || message.includes("有效期"))) {
        alert.showAlert({
          type: "error", 
          message: t("alert:cardExpiryError"),
        });
      } else if (isCorporateManager) {
        alert.showAlert({
          type: "error",
          message: t("alert:paymentFailedCheckReason"),
        });
        onOpenChange(false);
      } else {
        alert.showAlert({
          type: "error",
          message: t("alert:buyCreditFail"),
        });
      }
    }
  };

  useEffect(() => {
    numberStepperRef.current?.focus();
  }, []);

  return (
    <Dialog
      title={t("common:buyCredit")}
      open={open}
      onOpenChange={onOpenChange}
      width={640}
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => onOpenChange(false)}
          >
            <Typo typoType="btn3m">{t("common:cancel")}</Typo>
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={!isConfirm || isLoading}
            isLoading={isLoading}
          >
            {t("common:buy")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <Flex flexDirection="column" gap={40}>
        {/* Title */}
        <Flex flexDirection="column" alignItems="center" gap={12}>
          <Typo typoType="d6">{t("common:buyCreditTitle")}</Typo>
          <Typo typoType="b7m">{t("common:buyCreditTitleDescription")}</Typo>
        </Flex>

        {/* Content */}
        <CallOut backgroundColor="white">
          <SectionCardRow
            label={t("common:buyCreditAmount")}
            direction="horizontal"
            alignItems="center"
            labelWidth={194}
            value={
              <NumberStepper
                ref={numberStepperRef}
                value={creditAmount}
                min={1}
                max={50}
                onChange={(value) => setCreditAmount(value)}
              />
            }
          />
        </CallOut>

        <Flex flexDirection="column" gap={12}>
          <Typo typoType="h7">{t("common:price")}</Typo>
          <CallOut backgroundColor="white">
            <Flex flexDirection="column" gap={8} isFullWidth>
              {priceData.map(
                (
                  {
                    isDividerBefore,
                    labelTypoType,
                    labelColorType,
                    label,
                    valueTypoType,
                    value,
                    isDiscount,
                    isVisible = true,
                  },
                  index
                ) => (
                  <React.Fragment key={index}>
                    {isDividerBefore && <Divider />}
                    {isVisible && (
                      <SectionCardRow
                        label={
                          <Typo typoType={labelTypoType} color={labelColorType}>
                            {label}
                          </Typo>
                        }
                        labelWidth={194}
                        value={
                          <Flex>
                            <Typo typoType={valueTypoType}>
                              {value !== 0 && isDiscount && "-"}
                              {formatNumberWithDecimal(value)} USD
                            </Typo>
                          </Flex>
                        }
                      />
                    )}
                  </React.Fragment>
                )
              )}
            </Flex>
          </CallOut>
        </Flex>

        <Flex flexDirection="column" gap={12}>
          <Typo typoType="h7">{t("common:registeredPaymentMethod")}</Typo>
          <CallOut backgroundColor="white">
            <Flex flexDirection="column" gap={8} isFullWidth>
              {creditCardData.map(
                (
                  {
                    labelTypoType,
                    labelColorType,
                    label,
                    valueTypoType,
                    value,
                    isBusiness,
                    isBusinessBadge,
                  },
                  index
                ) => (
                  <React.Fragment key={index}>
                    <SectionCardRow
                      label={
                        <Typo typoType={labelTypoType} color={labelColorType}>
                          {label}
                        </Typo>
                      }
                      labelWidth={194}
                      value={
                        <Flex gap={8}>
                          <Typo typoType={valueTypoType}>{value}</Typo>
                          {isBusinessBadge && (
                            <Badge
                              badgeSize="S"
                              badgeColor="gray10"
                              color="gray4"
                              text={
                                isBusiness
                                  ? t(
                                      "subscriptionManagement:subscription.business"
                                    )
                                  : t(
                                      "subscriptionManagement:subscription.individual"
                                    )
                              }
                            />
                          )}
                        </Flex>
                      }
                    />
                  </React.Fragment>
                )
              )}
            </Flex>
          </CallOut>
        </Flex>

        {/* Notice */}
        <CallOut>
          <Flex flexDirection="column">
            {noticeList.map((text, idx) => {
              return (
                <Flex key={idx}>
                  <NoticeNumber typoType="b8r" color="gray4">
                    {idx + 1}.
                  </NoticeNumber>
                  <Typo typoType="b8r" color="gray4">
                    {text}
                  </Typo>
                </Flex>
              );
            })}
          </Flex>
        </CallOut>

        <CheckboxWithText>
          <Checkbox
            checked={isConfirm}
            id="isConfirm"
            onChange={() => setIsConfirm(!isConfirm)}
          />
          <label htmlFor="isConfirm">
            <Typo typoType="b7r">{t("common:buyCreditConfirm")}</Typo>
          </label>
        </CheckboxWithText>
      </Flex>
    </Dialog>
  );
}

export default BuyCreditDialog;

const NoticeNumber = styled(Typo)`
  min-width: 20px;
  text-align: center;
`;

const CheckboxWithText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    cursor: pointer;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colorSet.gray9};
  margin: 8px 0;
`;
